import { action, computed } from '@api'
import {WhiteboardStore} from '@modules/whiteboard'
import {
  TLBoundsEventHandler,
  TLBoundsHandleEventHandler,
  TLCanvasEventHandler,
  TLKeyboardEventHandler,
  TLPinchEventHandler,
  TLPointerEventHandler, TLShapeBlurHandler, TLShapeCloneHandler, TLWheelEventHandler,
  Utils
} from '@tldraw/core'
import { TDToolType } from '../types'

export type ToolStatus = 'idle' | 'creating' | 'pinching' | 'brushing' | 'editing-text' | 'transforming' | 'translating'
  | 'translatingHandle' | 'pointingBoundsHandle' | 'pointingBounds' | 'pointingHandle' | 'clonePainting' | 'spacePanning' | 'pointingClone' | 'pointingCanvas'
  | 'rotating' | 'translatingClone' | 'gridCloning' | 'pointing' | 'erasing'
//
// export enum TDStatus {
//   Idle = 'idle',
//   PointingHandle = 'pointingHandle',
//   PointingBounds = 'pointingBounds',
//   PointingBoundsHandle = 'pointingBoundsHandle',
//   TranslatingHandle = 'translatingHandle',
//   Translating = 'translating',
//   Transforming = 'transforming',
//   Rotating = 'rotating',
//   Pinching = 'pinching',
//   Brushing = 'brushing',
//   Creating = 'creating',
//   EditingText = 'editing-text',
// }
export class TDEventHandler {
  onPinchStart?: TLPinchEventHandler
  onPinchEnd?: TLPinchEventHandler
  onPinch?: TLPinchEventHandler
  onKeyDown?: TLKeyboardEventHandler
  onKeyUp?: TLKeyboardEventHandler
  onPointerMove?: TLPointerEventHandler
  onPointerUp?: TLPointerEventHandler
  onPan?: TLWheelEventHandler
  onZoom?: TLWheelEventHandler
  onPointerDown?: TLPointerEventHandler
  onPointCanvas?: TLCanvasEventHandler
  onDoubleClickCanvas?: TLCanvasEventHandler
  onRightPointCanvas?: TLCanvasEventHandler
  onDragCanvas?: TLCanvasEventHandler
  onReleaseCanvas?: TLCanvasEventHandler
  onPointShape?: TLPointerEventHandler
  onDoubleClickShape?: TLPointerEventHandler
  onRightPointShape?: TLPointerEventHandler
  onDragShape?: TLPointerEventHandler
  onHoverShape?: TLPointerEventHandler
  onUnhoverShape?: TLPointerEventHandler
  onReleaseShape?: TLPointerEventHandler
  onPointBounds?: TLBoundsEventHandler
  onDoubleClickBounds?: TLBoundsEventHandler
  onRightPointBounds?: TLBoundsEventHandler
  onDragBounds?: TLBoundsEventHandler
  onHoverBounds?: TLBoundsEventHandler
  onUnhoverBounds?: TLBoundsEventHandler
  onReleaseBounds?: TLBoundsEventHandler
  onPointBoundsHandle?: TLBoundsHandleEventHandler
  onDoubleClickBoundsHandle?: TLBoundsHandleEventHandler
  onRightPointBoundsHandle?: TLBoundsHandleEventHandler
  onDragBoundsHandle?: TLBoundsHandleEventHandler
  onHoverBoundsHandle?: TLBoundsHandleEventHandler
  onUnhoverBoundsHandle?: TLBoundsHandleEventHandler
  onReleaseBoundsHandle?: TLBoundsHandleEventHandler
  onPointHandle?: TLPointerEventHandler
  onDoubleClickHandle?: TLPointerEventHandler
  onRightPointHandle?: TLPointerEventHandler
  onDragHandle?: TLPointerEventHandler
  onHoverHandle?: TLPointerEventHandler
  onUnhoverHandle?: TLPointerEventHandler
  onReleaseHandle?: TLPointerEventHandler
  onShapeBlur?: TLShapeBlurHandler
  onShapeClone?: TLShapeCloneHandler
}


// eslint-disable-next-line @typescript-eslint/no-explicit-any
export abstract class BaseTool<T extends string = any> extends TDEventHandler {
  type: TDToolType | string = 'select' as const

  previous?: TDToolType | string

  constructor(public app: WhiteboardStore) {
    super()
  }

  get appState() { return this.app.appState}
  get status() { return this.appState.status}
  get tools() { return this.app.tools}
  cancelSession = () => this.tools.cancelSession()
  updateSession = () => this.tools.updateSession()
  completeSession = () => this.tools.completeSession()

  set status(status: ToolStatus) {
    this.app.appState.status = status
  }

  @action onEnter = () => {
    this.status ='idle'
  }

  @action onExit = () => {
    this.status = 'idle'
  }

  @action onCancel = () => {
    if (this.status === 'idle') {
      this.app.selectTool('select')
    } else {
      this.status = 'idle'
    }

    this.tools.cancelSession()
  }

  @computed get nextChildIndex() {
    const {
      shapes,
      currentPageId,
    } = this.app

    return shapes.length === 0
      ? 1
      : shapes
          .filter((shape) => shape.parentId === currentPageId)
          .sort((a, b) => b.childIndex - a.childIndex)[0].childIndex + 1
  }

  /* --------------------- Camera --------------------- */

  @action onPinchStart: TLPinchEventHandler = () => {
    this.tools.cancelSession()
    this.status = 'pinching'
  }

  @action onPinchEnd: TLPinchEventHandler = () => {
    if (Utils.isMobileSafari()) {
      this.app.selection.undoSelect()
    }
    this.status = 'idle'
  }

  @action onPinch: TLPinchEventHandler = (info, e) => {
    if (this.status !== 'pinching') return
    this.app.camera.pinchZoom(info.point, info.delta, info.delta[2])
    this.onPointerMove?.(info, e as unknown as React.PointerEvent)
  }

  /* ---------------------- Keys ---------------------- */

  @action onKeyDown: TLKeyboardEventHandler = (key) => {
    if (key === 'Escape') {
      this.onCancel()
      return
    }

    /* noop */
    if (key === 'Meta' || key === 'Control' || key === 'Alt') {
      this.tools.updateSession()
      return
    }
  }

  @action onKeyUp: TLKeyboardEventHandler = (key) => {
    /* noop */
    if (key === 'Meta' || key === 'Control' || key === 'Alt') {
      this.tools.updateSession()
      return
    }
  }

  /* --------------------- Pointer -------------------- */

  @action onPointerMove: TLPointerEventHandler = () => {
    if (this.status === 'creating') {
      this.tools.updateSession()
    }
  }

  @action onPointerUp: TLPointerEventHandler = () => {
    if (this.status === 'creating') {
      this.tools.completeSession()

      const { isToolLocked } = this.app.tools

      if (!isToolLocked) {
        this.app.selectTool('select')
      }
    }

    this.status = 'idle'
  }
}



// WEBPACK FOOTER //
// ./src/modules/whiteboard/tools/BaseTool.ts